import React, { useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { API } from "./components/atlas/helpers";
import { AtlasProvider, useAtlas } from "./store/atlasStore";

// Shell Layout components
import { Nav } from "./components/shell/Nav";
import { Topbar } from "./components/shell/Topbar";
import { CommandPalette } from "./components/shell/CommandPalette";

// Workspace components
import { OverviewWorkspace } from "./workspaces/OverviewWorkspace";
import { MarketsWorkspace } from "./workspaces/MarketsWorkspace";
import { ResearchWorkspace } from "./workspaces/ResearchWorkspace";
import { DecisionWorkspace } from "./workspaces/DecisionWorkspace";
import { PaperWorkspace } from "./workspaces/PaperWorkspace";
import { ModelsWorkspace } from "./workspaces/ModelsWorkspace";
import { FeaturesWorkspace } from "./workspaces/FeaturesWorkspace";
import { SystemWorkspace } from "./workspaces/SystemWorkspace";
import { OptionsWorkspace } from "./workspaces/OptionsWorkspace";
import { FuturesWorkspace } from "./workspaces/FuturesWorkspace";
import { WhaleHunterWorkspace } from "./workspaces/WhaleHunterWorkspace";
import { StatArbWorkspace } from "./workspaces/StatArbWorkspace";
import { LoginScreen } from "./components/auth/LoginScreen";

function WorkspaceShell() {
  const { state, dispatch } = useAtlas();
  const { workspace } = state;

  // Warmup symbol lists and features vocabulary at startup
  useEffect(() => {
    let alive = true;

    // Fetch symbol list
    axios.get(`${API}/market/symbols`)
      .then(r => {
        if (alive) dispatch({ type: "SET_SYMBOLS", payload: r.data });
      })
      .catch(e => console.warn("[Atlas] symbols fetch failed", e?.message));

    // Fetch registry feature stats
    axios.get(`${API}/registry/stats`)
      .then(r => {
        if (alive) dispatch({ type: "SET_FEAT_COUNT", payload: r.data.total });
      })
      .catch(e => console.warn("[Atlas] registry stats fetch failed", e?.message));

    return () => {
      alive = false;
    };
  }, [dispatch]);

function DiagnosticConsole() {
  const [open, setOpen] = React.useState(false);
  const [logs, setLogs] = React.useState([]);

  React.useEffect(() => {
    const interval = setInterval(() => {
      if (window.__logs && window.__logs.length !== logs.length) {
        setLogs([...window.__logs]);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [logs.length]);

  return (
    <div style={{
      position: "fixed", bottom: 10, right: 10, zIndex: 9999,
      background: "#0a0c0f", border: "1px solid var(--border-dim)", borderRadius: 6,
      boxShadow: "0 4px 20px rgba(0,0,0,0.5)", width: open ? 450 : 150,
      display: "flex", flexDirection: "column", overflow: "hidden", transition: "all 0.2s ease"
    }}>
      <div style={{
        background: "var(--surface)", borderBottom: "1px solid var(--border-dim)",
        padding: "6px 10px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer"
      }} onClick={() => setOpen(!open)}>
        <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--blue)" }}>
          Debug Console
        </span>
        <span className="badge badge-outline" style={{ fontSize: 8 }}>{logs.length} logs</span>
      </div>
      {open && (
        <div style={{
          height: 250, padding: 8, overflowY: "auto", fontFamily: "monospace", fontSize: 9,
          display: "flex", flexDirection: "column", gap: 4, background: "#050608"
        }}>
          {logs.map((log, i) => (
            <div key={i} style={{
              color: log.type === "error" ? "var(--red)" : log.type === "warn" ? "var(--amber)" : "var(--text-secondary)",
              lineHeight: 1.3, borderBottom: "1px solid rgba(255,255,255,0.03)", paddingBottom: 2
            }}>
              [{log.type.toUpperCase()}] {log.text}
            </div>
          ))}
          {logs.length === 0 && <div style={{ color: "var(--text-tertiary)" }}>No console output captured yet</div>}
        </div>
      )}
    </div>
  );
}

  return (
    <div className="atlas-shell">
      {/* Topbar */}
      <Topbar />

      {/* 52px Left Icon Nav */}
      <Nav />

      {/* Workspaces area */}
      <main className="atlas-workspace">
        {workspace === "overview" && <OverviewWorkspace />}
        {workspace === "markets"  && <MarketsWorkspace />}
        {workspace === "research" && <ResearchWorkspace />}
        {workspace === "decision" && <DecisionWorkspace />}
        {workspace === "paper"    && <PaperWorkspace />}
        {workspace === "futures"  && <FuturesWorkspace />}
        {workspace === "options"  && <OptionsWorkspace />}
        {workspace === "whale"    && <WhaleHunterWorkspace />}
        {workspace === "statarb"  && <StatArbWorkspace />}
        {workspace === "models"   && <ModelsWorkspace />}
        {workspace === "features" && <FeaturesWorkspace />}
        {workspace === "system"   && <SystemWorkspace />}
      </main>

      {/* Global Command Palette */}
      <CommandPalette />

      {/* Floating diagnostic console */}
      <DiagnosticConsole />
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = React.useState(
    !!localStorage.getItem("atlas_access_code")
  );
  const [toast, setToast] = React.useState(null);

  React.useEffect(() => {
    const handleLogout = () => setIsAuthenticated(false);
    const handleForbidden = (e) => {
      setToast({ message: e.detail || "Action not permitted.", type: "error" });
      setTimeout(() => setToast(null), 4000);
    };

    window.addEventListener("atlas-unauthorized", handleLogout);
    window.addEventListener("atlas-forbidden", handleForbidden);
    
    return () => {
      window.removeEventListener("atlas-unauthorized", handleLogout);
      window.removeEventListener("atlas-forbidden", handleForbidden);
    };
  }, []);

  if (!isAuthenticated) {
    return <LoginScreen onLogin={() => setIsAuthenticated(true)} />;
  }

  return (
    <AtlasProvider>
      <WorkspaceShell />
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 99999,
          background: toast.type === "error" ? "rgba(220, 38, 38, 0.9)" : "rgba(37, 99, 235, 0.9)",
          color: "white", padding: "12px 24px", borderRadius: 8, fontWeight: 500, fontSize: 14,
          boxShadow: "0 10px 40px rgba(0,0,0,0.5)", backdropFilter: "blur(10px)",
          border: `1px solid ${toast.type === "error" ? "rgba(239, 68, 68, 0.5)" : "rgba(59, 130, 246, 0.5)"}`,
          display: "flex", alignItems: "center", gap: 12
        }}>
          {toast.type === "error" && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          )}
          {toast.message}
        </div>
      )}
    </AtlasProvider>
  );
}

export default App;
